import styled from '@emotion/styled';
import { formatNumberGroupingSeparators } from '@vmos2/core';
import { LayoutCard } from '@vmos2/design-system/src/sections/LayoutCard';
import { Text } from '@vmos2/design-system/src/text/Text';
import { useComponentTheme } from '@vmos2/design-system/src/useComponentTheme';
import { DEFAULT_ZOOM_FACTOR, useAccessibilityContext } from '@vmos2/providers';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  className?: string,
  hasKioskOrShallowUser: boolean,
  kioskAuthButton: React.JSX.Element,
  kioskUser?: {
    profile: { firstName: string },
    uuid: string,
  },
  logo?: Node,
  loyaltyData: {
    earn: { pointsPerCurrencyUnit: number },
    points: number,
    provider: string,
    spend: { pointsPerPromo: number },
  },
  overview?: boolean,
  processingOrder: boolean,
  shallowKioskUser: Record<string, string>,
  showLoyaltyText?: boolean,
};

export const KioskAuth = ({
  className,
  hasKioskOrShallowUser = false,
  kioskAuthButton: KioskAuthButton,
  kioskUser,
  logo,
  loyaltyData,
  overview,
  processingOrder,
  shallowKioskUser,
  showLoyaltyText = true,
}: Props) => {
  const { zoomFactor } = useAccessibilityContext();
  const { t } = useTranslation('kioskAuth');
  const { t: tCommon } = useTranslation('common');
  const isUserLoggedIn = !isEmpty(kioskUser) || !isEmpty(shallowKioskUser);
  const isZoomed = zoomFactor && zoomFactor !== DEFAULT_ZOOM_FACTOR;
  const mode = 'KioskHeader';

  const { loyaltyTextVisible } = useComponentTheme(
    ({ components }) => components.KioskAuth,
    hasKioskOrShallowUser ? 'LoggedIn' : 'LoggedOut'
  );

  const renderUserContent = showText => (
    <Content data-test="kiosk-auth-logged-in">
      {showLoyaltyText && logo}

      {showText && showLoyaltyText && (
        <Text dataTest="kiosk-auth-logged-in-welcome" groupContentForScreenReader>
          {loyaltyData.provider !== 'vmos' && !!loyaltyData.points && (
            <Points marginLeft={0} marginRight={16} mode={mode}>
              {formatNumberGroupingSeparators(loyaltyData.points)}
              <span className="hide-text">{tCommon('point', { count: loyaltyData.points })}.</span>
            </Points>
          )}

          <Text mode={mode}>
            {kioskUser?.profile?.firstName
              ? t('welcomeBack', { firstName: kioskUser.profile.firstName })
              : t('welcomeBackUnknownUser')}
          </Text>

          {!!loyaltyData.spend.pointsPerPromo &&
            !!loyaltyData.earn.pointsPerCurrencyUnit &&
            loyaltyData.provider === 'vmos' && (
              <Points data-test="kiosk-auth-points" mode={mode}>
                {tCommon('point_titleCase', { count: loyaltyData.points })}:{' '}
                {formatNumberGroupingSeparators(loyaltyData.points)}
              </Points>
            )}
        </Text>
      )}
    </Content>
  );

  if (overview && isUserLoggedIn) {
    return <LayoutCardWrapper className={className}>{renderUserContent(true)}</LayoutCardWrapper>;
  }

  if (isUserLoggedIn) {
    return <Wrapper>{renderUserContent(loyaltyTextVisible)}</Wrapper>;
  }

  return (
    <Wrapper data-test="kiosk-auth-logged-out">
      {!isZoomed && (
        <>
          {showLoyaltyText && logo}

          {showLoyaltyText && loyaltyTextVisible && (
            <Text dataTest="kiosk-auth-logged-out-earn-points" mode={mode} mr={6}>
              {t('earnLoyaltyPoints', { slug: tCommon('point', { context: 'plural' }) })}
            </Text>
          )}
        </>
      )}

      <KioskAuthButton disabled={processingOrder} />
    </Wrapper>
  );
};

const Content = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSize.xl};
`;

const LayoutCardWrapper = styled(LayoutCard)`
  margin-bottom: 32px;
`;

const Points = styled(Text)`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-left: ${({ marginLeft = 16 }) => marginLeft}px;
  margin-right: ${({ marginRight = 0 }) => marginRight}px;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  font-size: ${({ theme }) => theme.fontSize.xl};
`;
